import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider } from "@/lib/auth-context";
import { ThemeProvider } from "@/lib/theme-context";
import { CommandPaletteProvider } from "@/components/CommandPalette";
import { Toaster } from "@/components/ui/sonner";
import ProtectedRoute from "@/components/ProtectedRoute";
import AuthCallback from "@/components/AuthCallback";
import { registerServiceWorker } from "@/lib/pwa";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Dashboard from "@/pages/Dashboard";
import TeachingAssistant from "@/pages/TeachingAssistant";
import OCRScanner from "@/pages/OCRScanner";
import ContentStudio from "@/pages/ContentStudio";
import Assessment from "@/pages/Assessment";
import Attendance from "@/pages/Attendance";
import KnowledgeHub from "@/pages/KnowledgeHub";
import Settings from "@/pages/Settings";
import Institution from "@/pages/Institution";
import ParentComms from "@/pages/ParentComms";
import AcceptInvitation from "@/pages/AcceptInvitation";
import TeachFromAnything from "@/pages/TeachFromAnything";
import LessonList from "@/pages/LessonList";
import LessonWorkspace from "@/pages/LessonWorkspace";
import Preferences from "@/pages/Preferences";
import LibraryPage from "@/pages/LibraryPage";
import Explore from "@/pages/Explore";
import PublicLesson from "@/pages/PublicLesson";
import Wallet from "@/pages/Wallet";
import ReferAndEarn from "@/pages/ReferAndEarn";
import ProfileEditor from "@/pages/ProfileEditor";
import PublicProfile from "@/pages/PublicProfile";
import ResumeBuilder from "@/pages/ResumeBuilder";
import CareerCopilot from "@/pages/CareerCopilot";
import PublicResume from "@/pages/PublicResume";
import AdminDashboard from "@/pages/AdminDashboard";
import HiringDashboard from "@/pages/HiringDashboard";
import DiscoverEducators from "@/pages/DiscoverEducators";
import EducatorDetail from "@/pages/EducatorDetail";
import NewJobPost from "@/pages/NewJobPost";
import MyJobPosts from "@/pages/MyJobPosts";
import CVRequests from "@/pages/CVRequests";
import JobsFeed from "@/pages/JobsFeed";
import ApplicationsKanban from "@/pages/ApplicationsKanban";
import HiringTeam from "@/pages/HiringTeam";
import MarketplacePublic from "@/pages/MarketplacePublic";
import About from "@/pages/About";
import LegalPage, { LegalCenter } from "@/pages/LegalPage";
import CookieConsent from "@/components/CookieConsent";
import BuddyBee from "@/components/BuddyBee";
import SplashScreen from "@/components/SplashScreen";
import FullTeachingKit from "@/pages/FullTeachingKit";
import DifferentiatedPack from "@/pages/DifferentiatedPack";
import GrowthStudio from "@/pages/GrowthStudio";
import GrowthBranding from "@/pages/GrowthBranding";
import GrowthPortfolio from "@/pages/GrowthPortfolio";
import GrowthDemoLesson from "@/pages/GrowthDemoLesson";
import GrowthInterviewPrep from "@/pages/GrowthInterviewPrep";
import GrowthReadiness from "@/pages/GrowthReadiness";
import GrowthCvImprove from "@/pages/GrowthCvImprove";
import PublicPortfolio from "@/pages/PublicPortfolio";
import Inbox from "@/pages/Inbox";
import ConnectionChat from "@/pages/ConnectionChat";

registerServiceWorker();

function AppRouter() {
  const location = useLocation();
  // CRITICAL: synchronous detection of OAuth callback (before ProtectedRoute checks)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/accept-invitation" element={<AcceptInvitation />} />

      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/teaching-assistant"
        element={
          <ProtectedRoute>
            <TeachingAssistant />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ocr"
        element={
          <ProtectedRoute>
            <OCRScanner />
          </ProtectedRoute>
        }
      />
      <Route
        path="/content-studio"
        element={
          <ProtectedRoute>
            <ContentStudio />
          </ProtectedRoute>
        }
      />
      <Route
        path="/assessment"
        element={
          <ProtectedRoute>
            <Assessment />
          </ProtectedRoute>
        }
      />
      <Route
        path="/attendance"
        element={
          <ProtectedRoute>
            <Attendance />
          </ProtectedRoute>
        }
      />
      <Route
        path="/knowledge-hub"
        element={
          <ProtectedRoute>
            <KnowledgeHub />
          </ProtectedRoute>
        }
      />
      <Route
        path="/parent-comms"
        element={
          <ProtectedRoute>
            <ParentComms />
          </ProtectedRoute>
        }
      />
      <Route
        path="/institution"
        element={
          <ProtectedRoute>
            <Institution />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/teach-from-anything"
        element={
          <ProtectedRoute>
            <TeachFromAnything />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lessons"
        element={
          <ProtectedRoute>
            <LessonList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lesson/:lessonId"
        element={
          <ProtectedRoute>
            <LessonWorkspace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/preferences"
        element={
          <ProtectedRoute>
            <Preferences />
          </ProtectedRoute>
        }
      />
      <Route
        path="/library"
        element={
          <ProtectedRoute>
            <LibraryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/explore"
        element={<Explore />}
      />
      <Route
        path="/wallet"
        element={
          <ProtectedRoute>
            <Wallet />
          </ProtectedRoute>
        }
      />
      <Route
        path="/refer"
        element={
          <ProtectedRoute>
            <ReferAndEarn />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfileEditor />
          </ProtectedRoute>
        }
      />
      <Route
        path="/resume"
        element={
          <ProtectedRoute>
            <ResumeBuilder />
          </ProtectedRoute>
        }
      />
      <Route
        path="/career"
        element={
          <ProtectedRoute>
            <CareerCopilot />
          </ProtectedRoute>
        }
      />
      <Route
        path="/teach/kit"
        element={
          <ProtectedRoute>
            <FullTeachingKit />
          </ProtectedRoute>
        }
      />
      <Route
        path="/teach/kit/:kitId"
        element={
          <ProtectedRoute>
            <FullTeachingKit />
          </ProtectedRoute>
        }
      />
      <Route
        path="/teach/pack/:packId"
        element={
          <ProtectedRoute>
            <DifferentiatedPack />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route path="/hiring"            element={<ProtectedRoute><HiringDashboard /></ProtectedRoute>} />
      <Route path="/hiring/discover"   element={<ProtectedRoute><DiscoverEducators /></ProtectedRoute>} />
      <Route path="/hiring/educator/:user_id" element={<ProtectedRoute><EducatorDetail /></ProtectedRoute>} />
      <Route path="/hiring/jobs"       element={<ProtectedRoute><MyJobPosts /></ProtectedRoute>} />
      <Route path="/hiring/jobs/new"   element={<ProtectedRoute><NewJobPost /></ProtectedRoute>} />
      <Route path="/hiring/requests"   element={<ProtectedRoute><CVRequests /></ProtectedRoute>} />
      <Route path="/hiring/applications" element={<ProtectedRoute><ApplicationsKanban /></ProtectedRoute>} />
      <Route path="/hiring/team"       element={<ProtectedRoute><HiringTeam /></ProtectedRoute>} />
      <Route path="/jobs"              element={<ProtectedRoute><JobsFeed /></ProtectedRoute>} />
      <Route path="/cv-requests"       element={<ProtectedRoute><CVRequests /></ProtectedRoute>} />
      <Route path="/growth-studio"                     element={<ProtectedRoute><GrowthStudio /></ProtectedRoute>} />
      <Route path="/growth-studio/branding"            element={<ProtectedRoute><GrowthBranding /></ProtectedRoute>} />
      <Route path="/growth-studio/portfolio"           element={<ProtectedRoute><GrowthPortfolio /></ProtectedRoute>} />
      <Route path="/growth-studio/demo-lesson"         element={<ProtectedRoute><GrowthDemoLesson /></ProtectedRoute>} />
      <Route path="/growth-studio/interview-prep"      element={<ProtectedRoute><GrowthInterviewPrep /></ProtectedRoute>} />
      <Route path="/growth-studio/global-readiness"    element={<ProtectedRoute><GrowthReadiness /></ProtectedRoute>} />
      <Route path="/growth-studio/cv-improve"          element={<ProtectedRoute><GrowthCvImprove /></ProtectedRoute>} />
      <Route path="/inbox"                              element={<ProtectedRoute><Inbox /></ProtectedRoute>} />
      <Route path="/inbox/:connectId"                   element={<ProtectedRoute><ConnectionChat /></ProtectedRoute>} />
      <Route path="/marketplace"       element={<MarketplacePublic />} />
      <Route path="/about"             element={<About />} />
      {/* Legal & Trust Center — Phase 10c-C */}
      <Route path="/legal"             element={<LegalCenter />} />
      <Route path="/legal/:slug"       element={<LegalPage />} />
      {/* Public routes — no auth required */}
      <Route path="/l/:shareToken" element={<PublicLesson />} />
      <Route path="/u/:username"   element={<PublicProfile />} />
      <Route path="/r/:shareToken" element={<PublicResume />} />
      <Route path="/p/:username"   element={<PublicPortfolio />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <BrowserRouter>
          <AuthProvider>
            <CommandPaletteProvider>
              <AppRouter />
              <CookieConsent />
              <BuddyBee />
              <SplashScreen />
              <Toaster richColors closeButton position="top-center" />
            </CommandPaletteProvider>
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </div>
  );
}

export default App;
